import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-expenses-mileage';

export type Trip = components['schemas']['Trip'];
export type TripBody = components['schemas']['TripBody'];
export type TripCreate = components['schemas']['TripCreate'];
export type Rate = components['schemas']['Rate'];
export type Vehicle = components['schemas']['Vehicle'];
export type ListedVehicle = components['schemas']['ListedVehicle'];
export type VehicleCreate = components['schemas']['VehicleCreate'];
export type VehiclePatch = components['schemas']['VehiclePatch'];
export type SavedRoute = components['schemas']['SavedRoute'];
export type RouteBody = components['schemas']['RouteBody'];
export type AciTariff = components['schemas']['AciTariff'];
export type MileageSetting = components['schemas']['MileageSetting'];
export type MileageSettingBody = components['schemas']['MileageSettingBody'];
export type Fuel = components['schemas']['Fuel'];

export const FUELS: readonly Fuel[] = ['petrol', 'diesel', 'lpg', 'cng', 'hybrid', 'electric'];

/** What a mileage call came to: the value, or the HTTP status and the problem's `code`. */
export type Outcome<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly status: number; readonly code: string };

type Run<T> = () => Promise<{ data?: T; error?: unknown; response: Response }>;

function codeOf(error: unknown): string {
  return typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';
}

async function call<T>(run: Run<T>): Promise<Outcome<T>> {
  try {
    const { data, error, response } = await run();
    if (response.ok) return { ok: true, value: data as T };
    return { ok: false, status: response.status, code: codeOf(error) };
  } catch {
    return { ok: false, status: 0, code: '' };
  }
}

function items<T>(outcome: Outcome<{ items: T[] }>): Outcome<readonly T[]> {
  return outcome.ok ? { ok: true, value: outcome.value.items } : outcome;
}

export async function listVehicles(owner?: string): Promise<Outcome<readonly ListedVehicle[]>> {
  return items(
    await call(() =>
      apiClient.GET('/v1/expenses/vehicles', { params: { query: owner ? { owner } : {} } }),
    ),
  );
}

export function createVehicle(body: VehicleCreate): Promise<Outcome<Vehicle>> {
  return call(() => apiClient.POST('/v1/expenses/vehicles', { body }));
}

export function patchVehicle(vehicleId: string, body: VehiclePatch): Promise<Outcome<Vehicle>> {
  return call(() =>
    apiClient.PATCH('/v1/expenses/vehicles/{vehicleId}', {
      params: { path: { vehicleId } },
      body,
    }),
  );
}

export function getRate(date: string, vehicleId?: string): Promise<Outcome<Rate>> {
  return call(() =>
    apiClient.GET('/v1/expenses/mileage/rate', {
      params: { query: vehicleId ? { date, vehicleId } : { date } },
    }),
  );
}

export function createTrip(body: TripCreate): Promise<Outcome<Trip>> {
  return call(() => apiClient.POST('/v1/expenses/mileage/trips', { body }));
}

export function getTrip(expenseId: string): Promise<Outcome<Trip>> {
  return call(() =>
    apiClient.GET('/v1/expenses/mileage/trips/{expenseId}', { params: { path: { expenseId } } }),
  );
}

export function updateTrip(
  expenseId: string,
  version: string,
  body: TripBody,
): Promise<Outcome<Trip>> {
  return call(() =>
    apiClient.PATCH('/v1/expenses/mileage/trips/{expenseId}', {
      params: { path: { expenseId }, header: { 'If-Match': version } },
      body,
    }),
  );
}

export async function listRoutes(): Promise<Outcome<readonly SavedRoute[]>> {
  return items(await call(() => apiClient.GET('/v1/expenses/mileage/routes')));
}

export function createRoute(body: RouteBody): Promise<Outcome<SavedRoute>> {
  return call(() => apiClient.POST('/v1/expenses/mileage/routes', { body }));
}

export function patchRoute(routeId: string, body: RouteBody): Promise<Outcome<SavedRoute>> {
  return call(() =>
    apiClient.PATCH('/v1/expenses/mileage/routes/{routeId}', {
      params: { path: { routeId } },
      body,
    }),
  );
}

export async function deleteRoute(routeId: string): Promise<Outcome<null>> {
  const outcome = await call(() =>
    apiClient.DELETE('/v1/expenses/mileage/routes/{routeId}', { params: { path: { routeId } } }),
  );
  return outcome.ok ? { ok: true, value: null } : outcome;
}

export async function searchAciTariffs(
  year: number,
  q: string,
): Promise<Outcome<readonly AciTariff[]>> {
  return items(
    await call(() =>
      apiClient.GET('/v1/expenses/mileage/aci-tariffs', { params: { query: { year, q } } }),
    ),
  );
}

export function getMileageSetting(): Promise<Outcome<MileageSetting>> {
  return call(() => apiClient.GET('/v1/expenses/settings/mileage'));
}

export function putMileageSetting(body: MileageSettingBody): Promise<Outcome<MileageSetting>> {
  return call(() => apiClient.PUT('/v1/expenses/settings/mileage', { body }));
}
